import React, { useState, useEffect } from "react";
import "../../assets/NewFiles/Css/ProjectsDashboard.css";
import { useNavigate } from "react-router-dom";
import api from "../../axios/api";
import { FaPenSquare, FaCheck, FaImage, FaUserAlt, FaMap, FaPlus } from "react-icons/fa";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Grid,
  useTheme,
} from "@mui/material";
import Loader from "../common/Loader";
import Button from "../common/styled components/Button";

const ProjectsDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [page, setPage] = useState(1);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const rowsPerPage = 10;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  
  // For responsive design
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchProjects = async () => {
    setIsLoading(true);
    try {
      const response = await api.get("/api/project/getProjects");
      setProjects(response.data.projects);
      
      // Extract unique organizations from projects
      const orgSet = new Set();
      response.data.projects.forEach(project => {
        const orgs = project.organizationName.split(',');
        orgs.forEach(org => orgSet.add(org.trim()));
      });
      setOrganizations(Array.from(orgSet).sort());
      
      setFilteredProjects(response.data.projects);
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOrgChange = (event) => {
    const org = event.target.value;
    setSelectedOrg(org);
    setPage(1); // Reset to first page when filter changes
    
    if (org === '') {
      setFilteredProjects(projects);
    } else {
      const filtered = projects.filter(project => 
        project.organizationName.split(',').map(o => o.trim()).includes(org)
      );
      setFilteredProjects(filtered);
    }
  };

  const updateProjectStatus = async (projectId) => {
    setIsLoading(true);
    try {
      const response = await api.post(
        `/api/project/updateProjectStatus/${projectId}`
      );
      if (response.status === 200) {
        fetchProjects();
      }
    } catch (error) {
      console.error("Error updating project status:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Calculate pagination
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedProjects = filteredProjects.slice(startIndex, endIndex);
  const pageCount = Math.ceil(filteredProjects.length / rowsPerPage);

  useEffect(() => {
    fetchProjects();
  }, []);

  // Card view for mobile
  const ProjectCard = ({ project }) => {
    return (
      <Card sx={{ 
        mb: 2, 
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
        }
      }}>
        <CardContent>
          <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold', color: '#002F43' }}>
            {project.name}
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Duration:</strong>
            </Typography>
            <Typography variant="body2">
              {new Date(project.startDate).toLocaleDateString()} - {new Date(project.endDate).toLocaleDateString()}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Progress:</strong>
            </Typography>
            <Chip 
              label={`${project.total_trees_planted} / ${project.target}`} 
              size="small"
              sx={{ 
                bgcolor: '#7CB342',
                fontWeight: 'medium'
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Address:</strong>
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right', maxWidth: '60%' }}>
              {project.address}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Organization:</strong>
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right', maxWidth: '60%' }}>
              {project.organizationName}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            <IconButton 
              size="small"
              onClick={() => navigate(`/dashboard/projectdashboard/${project.project_id}`)}
              sx={{ bgcolor: '#f5f5f5' }}
              data-tooltip-id="mobile-edit-tooltip"
              data-tooltip-content="Edit Project"
            >
              <FaPenSquare color="#00a9d3" size="16px" />
            </IconButton>
            
            <IconButton 
              size="small"
              onClick={() => navigate(`/dashboard/projectdashboard/updateprojectgallery/${project.project_id}`)}
              sx={{ bgcolor: '#f5f5f5' }}
              data-tooltip-id="mobile-gallery-tooltip"
              data-tooltip-content="Manage Gallery"
            >
              <FaImage color="#00a9d3" size="16px" />
            </IconButton>
            
            <IconButton 
              size="small"
              onClick={() => navigate(`/dashboard/projectdashboard/projectArea/${project.project_id}`)}
              sx={{ bgcolor: '#f5f5f5' }}
              data-tooltip-id="mobile-area-tooltip"
              data-tooltip-content="Manage Area"
            >
              <FaMap color="#00a9d3" size="16px" />
            </IconButton>
            
            <IconButton 
              size="small"
              onClick={() => updateProjectStatus(project.project_id)}
              sx={{ bgcolor: '#f5f5f5' }}
              data-tooltip-id="mobile-status-tooltip"
              data-tooltip-content={project.is_active === "0" ? "Activate Project" : "Deactivate Project"}
            >
              <FaCheck color={project.is_active === "0" ? "red" : "#83d300"} size="16px" />
            </IconButton>
            
            <IconButton 
              size="small"
              onClick={() => navigate(`/dashboard/projectcontributors/${project.project_id}`)}
              sx={{ bgcolor: '#f5f5f5' }}
              data-tooltip-id="mobile-contributors-tooltip"
              data-tooltip-content="Contributors"
            >
              <FaUserAlt color="gray" size="16px" />
            </IconButton>
          </Box>
          <Tooltip id="mobile-edit-tooltip" />
          <Tooltip id="mobile-gallery-tooltip" />
          <Tooltip id="mobile-area-tooltip" />
          <Tooltip id="mobile-status-tooltip" />
          <Tooltip id="mobile-contributors-tooltip" />
        </CardContent>
      </Card>
    );
  };

  // Loading overlay component
  const LoadingOverlay = () => (
    <div className="loading-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <Loader />
    </div>
  );

  return (
    <div>    <div className="projects-dashboard" style={{ padding: isMobile ? '1rem' : '2rem' }}>
      {isLoading && <LoadingOverlay />}
      
      <div className={isLoading ? 'content-disabled' : ''} style={{
        pointerEvents: isLoading ? 'none' : 'auto',
        opacity: isLoading ? 0.6 : 1,
        transition: 'opacity 0.3s'
      }}>
        {/* Header section with create button and filter */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'stretch' : 'center', 
          mb: 3,
          gap: 2
        }}>
          <Button 
            onClick={() => navigate("/dashboard/projectdashboard/newproject")}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              width: isMobile ? '100%' : 'auto',
              padding: '10px 16px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            <FaPlus size="14px" />
            Create New Project
          </Button>

          <FormControl sx={{ 
            minWidth: isMobile ? '100%' : 200,
            maxWidth: isMobile ? '100%' : 300
          }} size="small">
            <InputLabel id="org-select-label">Filter by Organization</InputLabel>
            <Select
              labelId="org-select-label"
              value={selectedOrg}
              label="Filter by Organization"
              onChange={handleOrgChange}
            >
              <MenuItem value="">All Organizations</MenuItem>
              {organizations.map((org) => (
                <MenuItem key={org} value={org}>
                  {org}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Responsive content - Table for desktop, Cards for mobile */}
        {isMobile ? (
          // Mobile View - Card Layout
          <Box>
            {paginatedProjects.length > 0 ? (
              paginatedProjects.map((project) => (
                <ProjectCard key={project.project_id} project={project} />
              ))
            ) : (
              <Card sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1">No projects found</Typography>
              </Card>
            )}
          </Box>
        ) : (
          // Desktop View - Table Layout
          <TableContainer 
            component={Paper}
            sx={{ 
              boxShadow: '0 4px 8px rgba(0,0,0,0.08)',
              borderRadius: '8px',
              overflow: 'hidden'
            }}
          >
            <Table>
              <TableHead sx={{ backgroundColor: "#002F43" }}>
                <TableRow>
                  <TableCell style={{ color: "#fff", fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell style={{ color: "#fff", fontWeight: 'bold' }}>Duration</TableCell>
                  <TableCell style={{ color: "#fff", fontWeight: 'bold' }}>Planted / Target</TableCell>
                  <TableCell style={{ color: "#fff", fontWeight: 'bold' }}>Address</TableCell>
                  <TableCell style={{ color: "#fff", fontWeight: 'bold' }}>Organization</TableCell>
                  <TableCell style={{ color: "#fff", fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedProjects.length > 0 ? (
                  paginatedProjects.map((project) => (
                    <TableRow 
                      key={project.project_id}
                      hover
                      sx={{
                        '&:nth-of-type(odd)': {
                          backgroundColor: '#f9f9f9',
                        },
                        '&:last-child td, &:last-child th': {
                          border: 0,
                        },
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: '#f0f7fa',
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 'medium' }}>{project.name}</TableCell>
                      <TableCell sx={{minWidth: 150}}>
                        {new Date(project.startDate).toLocaleDateString()}
                        {" - "}
                        {new Date(project.endDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${project.total_trees_planted} / ${project.target}`}
                          size="small"
                          sx={{ 
                            bgcolor: '#7CB342',
                            fontWeight: 'medium'
                          }}
                        />
                      </TableCell>
                      <TableCell>{project.address}</TableCell>
                      <TableCell>{project.organizationName}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            data-tooltip-id="edit-tooltip"
                            data-tooltip-content="Edit Project"
                            onClick={() => navigate(`/dashboard/projectdashboard/${project.project_id}`)}
                            sx={{ 
                              bgcolor: '#f5f5f5',
                              '&:hover': { bgcolor: '#e0f7fa' }
                            }}
                          >
                            <FaPenSquare color="#00a9d3" size="18px" />
                          </IconButton>
                          
                          <IconButton
                            size="small"
                            data-tooltip-id="manage-gallery"
                            data-tooltip-content="Manage project gallery"
                            onClick={() => navigate(`/dashboard/projectdashboard/updateprojectgallery/${project.project_id}`)}
                            sx={{ 
                              bgcolor: '#f5f5f5',
                              '&:hover': { bgcolor: '#e0f7fa' }
                            }}
                          >
                            <FaImage color="#00a9d3" size="18px" />
                          </IconButton>
                          
                          <IconButton
                            size="small"
                            data-tooltip-id="manage-area"
                            data-tooltip-content="Manage project Area"
                            onClick={() => navigate(`/dashboard/projectdashboard/projectArea/${project.project_id}`)}
                            sx={{ 
                              bgcolor: '#f5f5f5',
                              '&:hover': { bgcolor: '#e0f7fa' }
                            }}
                          >
                            <FaMap color="#00a9d3" size="18px" />
                          </IconButton>
                          
                          <IconButton
                            size="small"
                            data-tooltip-id="active"
                            data-tooltip-content={project.is_active === "0" ? "Activate Project" : "Deactivate Project"}
                            onClick={() => updateProjectStatus(project.project_id)}
                            sx={{ 
                              bgcolor: '#f5f5f5',
                              '&:hover': { bgcolor: project.is_active === "0" ? '#ffebee' : '#f1f8e9' }
                            }}
                          >
                            <FaCheck color={project.is_active === "0" ? "red" : "#83d300"} size="18px" />
                          </IconButton>
                          
                          <IconButton
                            size="small"
                            data-tooltip-id="contributors-tooltip"
                            data-tooltip-content="Project Contributors"
                            onClick={() => navigate(`/dashboard/projectcontributors/${project.project_id}`)}
                            sx={{ 
                              bgcolor: '#f5f5f5',
                              '&:hover': { bgcolor: '#f5f5f5' }
                            }}
                          >
                            <FaUserAlt color="gray" size="18px" />
                          </IconButton>
                        </Box>
                        <Tooltip id="edit-tooltip" />
                        <Tooltip id="manage-gallery" />
                        <Tooltip id="manage-area" />
                        <Tooltip id="active" />
                        <Tooltip id="contributors-tooltip" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan="6" align="center">
                      No projects found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        {/* Pagination */}
        <Box sx={{ 
          display: "flex", 
          justifyContent: "center", 
          marginTop: 3,
          marginBottom: 2
        }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={handlePageChange}
            color="primary"
            className="pagination-container"
            size={isMobile ? "small" : "medium"}
          />
        </Box>
      </div>
    </div>
    </div>

  );
};

export default ProjectsDashboard;